export interface SignUpPayload {
  name: string;
  email: string;
  password: string;
  confirm_password: string;
}

export interface SignUpResponse {
  name?: string;
  username?: string;
  email?: string;
  message?: string;
  code?: number;
  data?: Record<string, unknown>;
  error?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface SignInPayload {
  email: string;
  password: string;
  rememberMe?: boolean;
}

export interface SignInResponse {
  message?: string;
  code?: number;
  data?: {
    user?: {
      id: number;
      name: string;
      email: string;
      // additional fields that can come from /api/settings/users/me/
      is_active?: boolean;
      pwd_reset_required?: boolean;
      groups?: string[];
      permissions?: unknown[];
      company_member_id?: number;
      [key: string]: unknown;
    };
    token?: {
      refresh: string;
      access: string;
    };
  };
  error?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface CurrentUserResponse {
  message?: string;
  code?: number;
  data?: {
    id: number;
    is_active: boolean;
    name: string;
    email: string;
    pwd_reset_required: boolean;
    groups: string[];
    permissions: unknown[];
    company_member_id: number;
    [key: string]: unknown;
  };
  error?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface CompanyMemberResponse {
  message?: string;
  code?: number;
  data?: {
    id: number;
    email: string;
    first_name: string;
    middle_name: string;
    last_name: string;
    designation: string;
    contact_number: string;
    gender?: string;
    company?: {
      id: number;
      name: string;
      profile_picture: string | null;
      website: string | null;
      email: string | null;
      contact_number: string | null;
      industry: string | null;
      country: string | null;
      city: string | null;
      address_line_1: string | null;
      address_line_2: string | null;
      about: string | null;
    };
    department?: {
      id: number;
      name: string;
    };
    profile_picture: number | { id: number } | null;
    group?: "company_admin" | "company_recruiter" | "company_hiring_manager";
    [key: string]: unknown;
  };
  error?: Record<string, unknown>;
  [key: string]: unknown;
}

export type FieldErrors = Record<string, string[]>;

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

export interface RefreshTokenResponse {
  refresh: string;
  access: string;
}

export async function refreshAccessToken(
  refreshToken: string,
): Promise<RefreshTokenResponse> {
  const endpoint = `${API_BASE_URL}/auth/token/refresh/`;

  const response = await fetch(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ refresh: refreshToken }),
  });

  const responseBody = await response.json().catch(() => null);

  if (!response.ok) {
    const parsedBody =
      typeof responseBody === "object" && responseBody !== null
        ? (responseBody as Record<string, unknown>)
        : {};

    const errorMessage =
      (typeof parsedBody.detail === "string" && parsedBody.detail) ||
      (typeof parsedBody.message === "string" && parsedBody.message) ||
      (typeof parsedBody.error === "string" && parsedBody.error) ||
      "Unable to refresh token. Please try again.";

    throw new Error(errorMessage);
  }

  return (responseBody as RefreshTokenResponse) ?? { refresh: refreshToken, access: "" };
}

export async function signUp(payload: SignUpPayload): Promise<SignUpResponse> {
  const endpoint = `${API_BASE_URL}/auth/signup/`;

  const response = await fetch(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  let responseBody: unknown = null;
  try {
    responseBody = await response.json();
  } catch {
    // Response without JSON body (e.g., 204).
  }

  if (!response.ok) {
    const parsedBody =
      typeof responseBody === "object" && responseBody !== null
        ? (responseBody as Record<string, unknown>)
        : {};

    const fieldErrors = (() => {
      const maybeErrors = parsedBody.error ?? parsedBody.errors;
      if (
        maybeErrors &&
        typeof maybeErrors === "object" &&
        !Array.isArray(maybeErrors)
      ) {
        const mapped = Object.entries(maybeErrors).reduce(
          (acc, [key, value]) => {
            if (Array.isArray(value)) {
              acc[key] = value.map((item) => String(item));
            } else if (typeof value === "string") {
              acc[key] = [value];
            }
            return acc;
          },
          {} as FieldErrors,
        );
        return Object.keys(mapped).length > 0 ? mapped : undefined;
      }
      return undefined;
    })();

    const errorMessage =
      (typeof parsedBody.detail === "string" && parsedBody.detail) ||
      (typeof parsedBody.message === "string" && parsedBody.message) ||
      (typeof parsedBody.error === "string" && parsedBody.error) ||
      "Unable to complete sign up. Please try again.";

    const error = new Error(errorMessage);
    if (fieldErrors) {
      (error as Error & { fieldErrors: FieldErrors }).fieldErrors = fieldErrors;
    }
    throw error;
  }

  return (responseBody as SignUpResponse) ?? {};
}

export async function signIn(payload: SignInPayload): Promise<SignInResponse> {
  const { email, password } = payload;
  const endpoint = `${API_BASE_URL}/auth/token/`;

  const response = await fetch(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ email, password }),
  });

  const responseBody = await response.json().catch(() => null);

  if (!response.ok) {
    const parsedBody =
      typeof responseBody === "object" && responseBody !== null
        ? (responseBody as Record<string, unknown>)
        : {};

    const fieldErrors = (() => {
      const maybeErrors = parsedBody.error ?? parsedBody.errors;
      if (
        maybeErrors &&
        typeof maybeErrors === "object" &&
        !Array.isArray(maybeErrors)
      ) {
        const mapped = Object.entries(maybeErrors).reduce(
          (acc, [key, value]) => {
            if (Array.isArray(value)) {
              acc[key] = value.map((item) => String(item));
            } else if (typeof value === "string") {
              acc[key] = [value];
            }
            return acc;
          },
          {} as FieldErrors,
        );
        return Object.keys(mapped).length > 0 ? mapped : undefined;
      }
      return undefined;
    })();

    const errorMessage =
      (typeof parsedBody.detail === "string" && parsedBody.detail) ||
      (typeof parsedBody.message === "string" && parsedBody.message) ||
      (typeof parsedBody.error === "string" && parsedBody.error) ||
      "Unable to sign in. Please try again.";

    const error = new Error(errorMessage);
    if (fieldErrors) {
      (error as Error & { fieldErrors: FieldErrors }).fieldErrors = fieldErrors;
    }
    throw error;
  }

  return (responseBody as SignInResponse) ?? {};
}

export async function fetchCurrentUser(
  accessToken: string,
): Promise<CurrentUserResponse> {
  const endpoint = `${API_BASE_URL}/api/settings/users/me/`;

  const { authenticatedFetch } = await import("./apiInterceptor");
  const response = await authenticatedFetch(endpoint, {
    method: "GET",
  });

  const responseBody = await response.json().catch(() => null);

  if (!response.ok) {
    const parsedBody =
      typeof responseBody === "object" && responseBody !== null
        ? (responseBody as Record<string, unknown>)
        : {};

    const errorMessage =
      (typeof parsedBody.detail === "string" && parsedBody.detail) ||
      (typeof parsedBody.message === "string" && parsedBody.message) ||
      (typeof parsedBody.error === "string" && parsedBody.error) ||
      "Unable to fetch current user. Please try again.";

    throw new Error(errorMessage);
  }

  return (responseBody as CurrentUserResponse) ?? {};
}

export async function fetchCompanyMember(
  memberId: number,
  accessToken: string,
): Promise<CompanyMemberResponse> {
  const endpoint = `${API_BASE_URL}/api/company/member/${memberId}/`;

  const { authenticatedFetch } = await import("./apiInterceptor");
  const response = await authenticatedFetch(endpoint, {
    method: "GET",
  });

  const responseBody = await response.json().catch(() => null);

  if (!response.ok) {
    const parsedBody =
      typeof responseBody === "object" && responseBody !== null
        ? (responseBody as Record<string, unknown>)
        : {};

    const errorMessage =
      (typeof parsedBody.detail === "string" && parsedBody.detail) ||
      (typeof parsedBody.message === "string" && parsedBody.message) ||
      (typeof parsedBody.error === "string" && parsedBody.error) ||
      "Unable to fetch company member. Please try again.";

    throw new Error(errorMessage);
  }

  return (responseBody as CompanyMemberResponse) ?? {};
}

export async function updateCompanyMember(
  memberId: number,
  accessToken: string,
  payload: {
    first_name: string;
    middle_name: string;
    last_name: string;
    designation: string;
    gender: string;
    department: number | null;
    contact_number?: string;
    profile_picture?: number | null;
    group?: "company_admin" | "company_recruiter" | "company_hiring_manager";
  },
): Promise<CompanyMemberResponse> {
  const endpoint = `${API_BASE_URL}/api/company/member/${memberId}/`;

  const { authenticatedFetch } = await import("./apiInterceptor");
  const response = await authenticatedFetch(endpoint, {
    method: "PUT",
    body: JSON.stringify(payload),
  });

  const responseBody = await response.json().catch(() => null);

  if (!response.ok) {
    const parsedBody =
      typeof responseBody === "object" && responseBody !== null
        ? (responseBody as Record<string, unknown>)
        : {};

    const errorMessage =
      (typeof parsedBody.detail === "string" && parsedBody.detail) ||
      (typeof parsedBody.message === "string" && parsedBody.message) ||
      (typeof parsedBody.error === "string" && parsedBody.error) ||
      "Unable to update company member. Please try again.";

    throw new Error(errorMessage);
  }

  return (responseBody as CompanyMemberResponse) ?? {};
}

export interface ResetPasswordPayload {
  token: string;
  password: string;
  confirm_password: string;
}

export interface ResetPasswordResponse {
  message?: string;
  code?: number;
  data?: unknown;
  error?: Record<string, unknown>;
  [key: string]: unknown;
}

export async function resetPassword(
  payload: ResetPasswordPayload
): Promise<ResetPasswordResponse> {
  const endpoint = `${API_BASE_URL}/auth/reset-password/`;

  const response = await fetch(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  const responseBody = await response.json().catch(() => null);

  if (!response.ok) {
    const parsedBody =
      typeof responseBody === "object" && responseBody !== null
        ? (responseBody as Record<string, unknown>)
        : {};

    const errorMessage =
      (typeof parsedBody.detail === "string" && parsedBody.detail) ||
      (typeof parsedBody.message === "string" && parsedBody.message) ||
      (typeof parsedBody.error === "string" && parsedBody.error) ||
      "Unable to reset password. Please try again.";

    throw new Error(errorMessage);
  }

  return (responseBody as ResetPasswordResponse) ?? {};
}

export interface ChangePasswordPayload {
  new_password: string;
  confirm_password: string;
}

export interface ChangePasswordResponse {
  message?: string;
  code?: number;
  data?: unknown;
  error?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface ChangeCurrentPasswordPayload {
  old_password: string;
  new_password: string;
  confirm_password: string;
}

export async function changePassword(
  payload: ChangePasswordPayload,
  accessToken: string
): Promise<ChangePasswordResponse> {
  const endpoint = `${API_BASE_URL}/auth/change-password/`;

  const response = await fetch(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(payload),
  });

  const responseBody = await response.json().catch(() => null);

  if (!response.ok) {
    const parsedBody =
      typeof responseBody === "object" && responseBody !== null
        ? (responseBody as Record<string, unknown>)
        : {};

    const errorMessage =
      (typeof parsedBody.detail === "string" && parsedBody.detail) ||
      (typeof parsedBody.message === "string" && parsedBody.message) ||
      (typeof parsedBody.error === "string" && parsedBody.error) ||
      "Unable to change password. Please try again.";

    throw new Error(errorMessage);
  }

  return (responseBody as ChangePasswordResponse) ?? {};
}

export async function changeCurrentPassword(
  payload: ChangeCurrentPasswordPayload
): Promise<ChangePasswordResponse> {
  const endpoint = `${API_BASE_URL}/api/settings/users/change-password/`;

  const { authenticatedFetch } = await import("./apiInterceptor");
  const response = await authenticatedFetch(endpoint, {
    method: "POST",
    body: JSON.stringify(payload),
  });

  const responseBody = await response.json().catch(() => null);

  if (!response.ok) {
    const parsedBody =
      typeof responseBody === "object" && responseBody !== null
        ? (responseBody as Record<string, unknown>)
        : {};

    const nonFieldErrors = parsedBody.error && typeof parsedBody.error === "object"
      ? (parsedBody.error as Record<string, unknown>).non_field_errors
      : undefined;

    const normalizedNonFieldError =
      Array.isArray(nonFieldErrors) && nonFieldErrors.length > 0
        ? String(nonFieldErrors[0]).replace(/^\[['"]?/, "").replace(/['"]?\]$/, "")
        : undefined;

    const errorMessage =
      (typeof normalizedNonFieldError === "string" && normalizedNonFieldError) ||
      (typeof parsedBody.detail === "string" && parsedBody.detail) ||
      (typeof parsedBody.message === "string" && parsedBody.message) ||
      (typeof parsedBody.error === "string" && parsedBody.error) ||
      "Unable to change password. Please try again.";

    throw new Error(errorMessage);
  }

  return (responseBody as ChangePasswordResponse) ?? {};
}

export interface ForgotPasswordPayload {
  email: string;
}

export interface ForgotPasswordResponse {
  message?: string;
  code?: number;
  data?: {
    token: string;
  };
  error?: Record<string, unknown>;
  [key: string]: unknown;
}

export async function forgotPassword(
  payload: ForgotPasswordPayload
): Promise<ForgotPasswordResponse> {
  const endpoint = `${API_BASE_URL}/auth/forgot-password/`;

  const response = await fetch(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  const responseBody = await response.json().catch(() => null);

  if (!response.ok) {
    const parsedBody =
      typeof responseBody === "object" && responseBody !== null
        ? (responseBody as Record<string, unknown>)
        : {};

    const errorMessage =
      (typeof parsedBody.detail === "string" && parsedBody.detail) ||
      (typeof parsedBody.message === "string" && parsedBody.message) ||
      (typeof parsedBody.error === "string" && parsedBody.error) ||
      "Unable to send password reset email. Please try again.";

    const normalizedErrorMessage =
      errorMessage === "Something went wrong. Please contact to Administrator"
        ? "Please contact to Administrator."
        : errorMessage;

    throw new Error(normalizedErrorMessage);
  }

  return (responseBody as ForgotPasswordResponse) ?? {};
}

export interface CompanyResponse {
  message?: string;
  code?: number;
  data?: {
    id: number;
    name: string;
    profile_picture: number | null;
    profile_picture_url: string | null;
    website: string | null;
    email: string | null;
    contact_number: string | null;
    industry: { id: number; name: string } | null;
    country: { id: number; name: string } | null;
    city: { id: number; name: string } | null;
    region: { id: number; name: string } | null;
    address_line_1: string | null;
    address_line_2: string | null;
    about: string | null;
  };
  error?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface UpdateCompanyPayload {
  name: string;
  website?: string | null;
  email?: string | null;
  contact_number?: string | null;
  industry?: number | null;
  country?: number | null;
  city?: number | null;
  address_line_1?: string | null;
  address_line_2?: string | null;
  about?: string | null;
  profile_picture?: number | null;
  departments?: number[];
}

export async function fetchCompany(
  companyId: number,
  accessToken: string,
): Promise<CompanyResponse> {
  const endpoint = `${API_BASE_URL}/api/company/company/${companyId}/`;

  const { authenticatedFetch } = await import("./apiInterceptor");
  const response = await authenticatedFetch(endpoint, {
    method: "GET",
  });

  const responseBody = await response.json().catch(() => null);

  if (!response.ok) {
    const parsedBody =
      typeof responseBody === "object" && responseBody !== null
        ? (responseBody as Record<string, unknown>)
        : {};

    const errorMessage =
      (typeof parsedBody.detail === "string" && parsedBody.detail) ||
      (typeof parsedBody.message === "string" && parsedBody.message) ||
      (typeof parsedBody.error === "string" && parsedBody.error) ||
      "Unable to fetch company. Please try again.";

    throw new Error(errorMessage);
  }

  return (responseBody as CompanyResponse) ?? {};
}

export async function updateCompany(
  companyId: number,
  accessToken: string,
  payload: UpdateCompanyPayload,
): Promise<CompanyResponse> {
  const endpoint = `${API_BASE_URL}/api/company/company/${companyId}/`;

  const { authenticatedFetch } = await import("./apiInterceptor");
  const response = await authenticatedFetch(endpoint, {
    method: "PUT",
    body: JSON.stringify(payload),
  });

  const responseBody = await response.json().catch(() => null);

  if (!response.ok) {
    const parsedBody =
      typeof responseBody === "object" && responseBody !== null
        ? (responseBody as Record<string, unknown>)
        : {};

    const errorMessage =
      (typeof parsedBody.detail === "string" && parsedBody.detail) ||
      (typeof parsedBody.message === "string" && parsedBody.message) ||
      (typeof parsedBody.error === "string" && parsedBody.error) ||
      "Unable to update company. Please try again.";

    throw new Error(errorMessage);
  }

  return (responseBody as CompanyResponse) ?? {};
}

